import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bug, Shield, Phone, Check, MapPin, ChevronRight, Home, AlertTriangle, Calculator, RotateCcw, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Header, Logo } from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { usePestPricing } from "../hooks/usePestPricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO Component
const PageSEO = () => (
  <>
    <title>Pest Control Ripley | Local Treatments | Pest Free</title>
    <meta name="description" content="Professional pest control in Ripley, QLD. Cockroaches, ants, spiders, bed bugs & rodents in Ripley estates and surrounds. QBCC Licensed." />
    <meta name="keywords" content="pest control Ripley, cockroach control Ripley QLD, ant treatment Ripley, spider control Ripley, pest control Providence, pest control Ecco Ripley" />
    <link rel="canonical" href="https://pestfreebrisbane.com.au/pest-control-ripley" />
    <meta property="og:title" content="Pest Control Ripley | Pest Free Ipswich & Brisbane" />
    <meta property="og:description" content="Professional pest control services in Ripley. Safe for pets & children. Call (07) 3708 1422." />
    <meta property="og:url" content="https://pestfreebrisbane.com.au/pest-control-ripley" />
  </>
);

// Theme colors for pests - Red
const theme = {
  primary: "#E74C3C",
  primaryDark: "#C0392B",
  primaryLight: "#E74C3C20",
  gradient: "from-[#E74C3C] to-[#922B21]"
};

export default function PestControlRipleyPage() {
  const calculatorRef = useRef(null);
  
  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const pestTypes = [
    { name: "Cockroaches", desc: "German, American, and Australian species" },
    { name: "Ants", desc: "Black ants, coastal brown ants, fire ants" },
    { name: "Spiders", desc: "Redbacks, huntsmans, white-tails" },
    { name: "Bed Bugs", desc: "Complete elimination and prevention" },
    { name: "Rodents", desc: "Rats and mice control" },
    { name: "Silverfish", desc: "Protect your books and fabrics" },
    { name: "Fleas", desc: "Pet and home flea treatments" },
    { name: "Mosquitoes", desc: "Fogging with professional equipment" }
  ];

  const ripleyAreas = [
    "Ripley Valley",
    "Providence",
    "Ecco Ripley", 
    "Ripley Town Centre",
    "Deebing Heights",
    "South Ripley"
  ];

  return (
    <div className="min-h-screen">
      <PageSEO />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="ripley-pest-hero">
        <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient}`} />
        <div className="absolute inset-0 bg-slate-900/20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <MapPin className="w-5 h-5 text-white" />
              <span className="font-medium text-white">Servicing Ripley & Surrounds</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight text-white">
              Pest Control <span className="text-yellow-300">Ripley</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Professional pest control for Ripley's growing communities. From cockroaches to rodents — we handle all pests with professional-grade treatments and equipment.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={scrollToCalculator}
                className="bg-white text-[#E74C3C] hover:bg-slate-100 text-lg px-8 py-6 rounded-full font-semibold shadow-lg flex items-center justify-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Get Instant Price
              </Button>
              <a 
                href="tel:0737081422"
                className="inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white border-2 border-white px-8 py-4 rounded-full text-lg font-semibold transition-all"
              >
                <Phone className="w-5 h-5" />
                (07) 3708 1422
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ripley-Specific Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
                Local Pest Control for <span style={{ color: theme.primary }}>Ripley Estates</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Ripley is one of South East Queensland's fastest-growing areas, with new estates like Providence, Ecco Ripley, and South Ripley bringing thousands of new homes. With rapid development and surrounding open bushland, pest pressure in Ripley is significant.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Our team understands the unique pest challenges facing Ripley homeowners — from construction-disturbed ant colonies to rodents displaced by land clearing. We provide professional pest management tailored to new builds and established properties alike.
              </p>
              <Link 
                to="/pests"
                className="inline-flex items-center gap-2 text-[#E74C3C] font-semibold hover:underline"
              >
                View our full Pest Control services <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#E74C3C]" />
                Common Pest Issues in Ripley
              </h3>
              <ul className="space-y-3">
                {[
                  "Ant infestations from disturbed soil during construction",
                  "Cockroaches moving into new builds seeking shelter",
                  "Spiders (especially redbacks) in new estate gardens",
                  "Rodents displaced by nearby land clearing",
                  "Mosquitoes breeding in drainage areas",
                  "Fleas from local wildlife in bushland borders"
                ].map((issue, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#E74C3C] mt-0.5 shrink-0" />
                    <span className="text-slate-600">{issue}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pests We Treat */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Pests We Treat in <span style={{ color: theme.primary }}>Ripley</span>
            </h2>
            <p className="text-lg text-slate-600">Complete pest management for homes and businesses</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {pestTypes.map((pest, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 hover:border-[#E74C3C]/30 hover:shadow-md transition-all">
                <h3 className="font-semibold text-slate-900 mb-1">{pest.name}</h3>
                <p className="text-sm text-slate-500">{pest.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              What's Included in Our <span style={{ color: theme.primary }}>Pest Treatment</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Standard Treatment</h3>
              <ul className="space-y-3">
                {[
                  "Internal treatment",
                  "External treatment",
                  "Harbourage areas (behind fridge, dishwasher, under sinks, subfloor)",
                  "Cockroach gel bait application (kitchen, bathrooms and infested areas)"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#7ABC32] mt-0.5 shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#7ABC32]/5 rounded-2xl p-8 border-2 border-[#7ABC32]/30">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-xl font-semibold text-slate-900">Comprehensive Treatment</h3>
                <span className="text-xs bg-[#7ABC32] text-white px-2 py-1 rounded-full">Recommended</span>
              </div>
              <ul className="space-y-3">
                {[
                  "Internal treatment",
                  "External treatment",
                  "Harbourage areas (behind fridge, dishwasher, under sinks, subfloor)",
                  "Cockroach gel bait application (kitchen, bathrooms and infested areas)",
                  "Fence / full perimeter treatment",
                  "Roof void dusting",
                  "3x cockroach pheromone sticky traps",
                  "Ant treatment included",
                  "Multiple professional-grade products used"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#7ABC32] mt-0.5 shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Button 
              onClick={scrollToCalculator}
              className="inline-flex items-center gap-2 bg-[#E74C3C] hover:bg-[#C0392B] text-white px-6 py-3 rounded-full font-semibold transition-all"
            >
              Get an Instant Price Quote <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              We Service All <span className="text-[#E74C3C]">Ripley Areas</span>
            </h2>
            <p className="text-slate-400">Plus surrounding suburbs including Springfield, Redbank Plains, and Ipswich</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {ripleyAreas.map((area, i) => (
              <span key={i} className="px-4 py-2 bg-white/10 rounded-full text-white font-medium">
                {area}
              </span>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-slate-400 mb-4">2-hour response time guaranteed for Ripley residents</p>
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <div ref={calculatorRef}>
        <PricingCalculator theme={theme} />
      </div>

      {/* Back to Main Page CTA */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 mb-4">Looking for more information about our pest control services?</p>
          <Link 
            to="/pests"
            className="inline-flex items-center gap-2 text-[#E74C3C] font-semibold hover:underline text-lg"
          >
            Visit our main Pest Control page <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// Pricing Calculator Component
function PricingCalculator({ theme }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [propertyType, setPropertyType] = useState("house");
  const [bedrooms, setBedrooms] = useState("3");
  const [storeys, setStoreys] = useState("single");
  const [treatmentType, setTreatmentType] = useState("standard");
  const [serviceFrequency, setServiceFrequency] = useState("oneoff");
  const [rodentTreatment, setRodentTreatment] = useState(false);
  const [termiteAddOn, setTermiteAddOn] = useState(false);
  
  // Contact form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [message, setMessage] = useState("");
  
  const { prices, loading } = usePestPricing({
    propertyType, bedrooms, storeys, treatmentType, serviceFrequency, rodentTreatment, termiteAddOn,
  });
  
  // Calculate launch discount (10% off total per visit price)
  const launchDiscount = Math.round(prices.pricePerVisit * 0.10);
  const discountedPrice = prices.pricePerVisit - launchDiscount;
  
  const handleReset = () => {
    setPropertyType("house");
    setBedrooms("3");
    setStoreys("single");
    setTreatmentType("standard");
    setServiceFrequency("oneoff");
    setRodentTreatment(false);
    setTermiteAddOn(false);
    setName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setMessage("");
    setSubmitted(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/pest-estimate`, {
        name,
        email,
        phone,
        address,
        propertyType,
        bedrooms: parseInt(bedrooms),
        storeys,
        treatmentType,
        serviceFrequency,
        rodentTreatment,
        rodentPrice: prices.rodentPrice || null,
        rodentStations: rodentTreatment ? (treatmentType === "comprehensive" ? 6 : 3) : null,
        rodentWarranty: rodentTreatment ? (treatmentType === "comprehensive" ? "6 months" : "3 months") : null,
        termiteAddOn,
        pricePerVisit: prices.pricePerVisit,
        annualTotal: prices.annualTotal || null,
        savings: prices.savings || null,
        termiteInspectionEstimate: prices.termiteInspection || null,
        termiteBarrierEstimate: prices.termiteBarrier || null,
        termiteBarrierDiscounted: prices.termiteBarrierDiscounted || null,
        message,
        source: "ripley-landing-page",
        timestamp: new Date().toISOString()
      });
      
      setSubmitted(true);
      toast.success("Estimate request sent! We'll be in touch shortly.");
    } catch (error) {
      console.error("Failed to submit estimate:", error);
      toast.error("Failed to submit. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="animate-pulse">Loading calculator...</div>
        </div>
      </section>
    );
  }
  
  return (
    <section className="py-20 bg-slate-50" data-testid="ripley-pest-calculator">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Logo className="h-16 mx-auto mb-4" variant="dark" />
          <div className="inline-flex items-center gap-2 bg-[#E74C3C]/10 rounded-full px-4 py-2 mb-4">
            <Calculator className="w-5 h-5 text-[#E74C3C]" />
            <span className="font-medium text-[#E74C3C]">Get Your Price in 60 Seconds</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
            Pest Control <span style={{ color: theme.primary }}>Calculator</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Get an instant estimate for your Ripley property
          </p>
        </div>
        
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="grid lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
            {/* Left Column - Calculator Options */}
            <div className="lg:col-span-3 p-6 md:p-8">
              <div className="space-y-8">
                {/* Property Type */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Property Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "house", label: "House" },
                      { value: "unit", label: "Unit / Townhouse" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setPropertyType(option.value)}
                        className={`p-4 rounded-xl border-2 text-center font-medium transition-all ${
                          propertyType === option.value
                            ? "border-[#E74C3C] bg-[#E74C3C]/5 text-[#E74C3C]"
                            : "border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Bedrooms */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Number of Bedrooms</Label>
                  <div className="grid grid-cols-6 gap-2">
                    {["1", "2", "3", "4", "5", "6"].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setBedrooms(num)}
                        className={`p-3 rounded-xl border-2 text-center font-semibold transition-all ${
                          bedrooms === num
                            ? "border-[#E74C3C] bg-[#E74C3C]/5 text-[#E74C3C]"
                            : "border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}
                      >
                        {num === "6" ? "6+" : num}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Storeys */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">House Height</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "single", label: "Single Storey" },
                      { value: "double", label: "Double Storey" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setStoreys(option.value)}
                        className={`p-4 rounded-xl border-2 text-center font-medium transition-all ${
                          storeys === option.value
                            ? "border-[#E74C3C] bg-[#E74C3C]/5 text-[#E74C3C]"
                            : "border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Service Frequency */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Service Frequency</Label>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setServiceFrequency("sixmonthly")}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        serviceFrequency === "sixmonthly"
                          ? "border-[#E74C3C] bg-[#E74C3C]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">6-Monthly Protection Plan</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-[#7ABC32] text-white px-2 py-1 rounded-full">Recommended</span>
                          {serviceFrequency === "sixmonthly" && <Check className="w-5 h-5 text-[#E74C3C]" />}
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">
                        Preventative maintenance every 6 months reduces infestations before they start.
                      </p>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setServiceFrequency("oneoff")}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        serviceFrequency === "oneoff"
                          ? "border-[#E74C3C] bg-[#E74C3C]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">Yearly Visits</span>
                        {serviceFrequency === "oneoff" && <Check className="w-5 h-5 text-[#E74C3C]" />}
                      </div>
                      <p className="text-sm text-slate-500">Single annual treatment.</p>
                    </button>
                  </div>
                </div>
                
                {/* Treatment Type */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Treatment Type</Label>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setTreatmentType("standard")}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        treatmentType === "standard"
                          ? "border-[#E74C3C] bg-[#E74C3C]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">Standard Treatment</span>
                        {treatmentType === "standard" && <Check className="w-5 h-5 text-[#E74C3C]" />}
                      </div>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> Internal & external treatment</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> Harbourage areas treated</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#E74C3C] mt-0.5 shrink-0" /> <span className="text-[#E74C3C] font-medium">3 month warranty</span></li>
                      </ul>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setTreatmentType("comprehensive")}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        treatmentType === "comprehensive"
                          ? "border-[#E74C3C] bg-[#E74C3C]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">Comprehensive Treatment</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-[#7ABC32] text-white px-2 py-1 rounded-full">Recommended</span>
                          {treatmentType === "comprehensive" && <Check className="w-5 h-5 text-[#E74C3C]" />}
                        </div>
                      </div>
                      <ul className="text-sm text-slate-600 space-y-1 mb-2">
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Full perimeter & roof void</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Ant treatment included</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> <span className="text-[#7ABC32] font-medium">6 month warranty</span></li>
                      </ul>
                    </button>
                  </div>
                </div>
                
                {/* Add-ons */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Optional Add-Ons</Label>
                  <div className="space-y-3">
                    <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      rodentTreatment ? "border-[#E74C3C] bg-[#E74C3C]/5" : "border-slate-200 hover:border-slate-300"
                    }`}>
                      <Checkbox checked={rodentTreatment} onCheckedChange={setRodentTreatment} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900">Rodent Treatment Add-On</span>
                          {rodentTreatment && (
                            <span className="text-sm font-semibold text-[#E74C3C]">
                              +${(() => {
                                let rodentPrice = 175;
                                const bedroomNum = parseInt(bedrooms);
                                if (bedroomNum <= 2) rodentPrice -= 25;
                                else if (bedroomNum >= 5) rodentPrice += 25;
                                if (storeys === "double") rodentPrice += 20;
                                if (treatmentType === "comprehensive") rodentPrice += 35;
                                return rodentPrice;
                              })()}
                            </span>
                          )}
                        </div>
                        {rodentTreatment && (
                          <p className="mt-2 text-sm text-slate-600">
                            {treatmentType === "comprehensive" 
                              ? "Inspection and installation of up to 6 tamper proof, lockable rodent bait stations"
                              : "Inspection and installation of up to 3 tamper proof, lockable rodent bait stations"
                            }
                          </p>
                        )}
                      </div>
                    </label>
                    
                    <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      termiteAddOn ? "border-[#D2691E] bg-[#D2691E]/5" : "border-slate-200 hover:border-slate-300"
                    }`}>
                      <Checkbox checked={termiteAddOn} onCheckedChange={setTermiteAddOn} className="mt-1" />
                      <div>
                        <span className="font-medium text-slate-900">Add Yearly Termite Inspection</span>
                        {termiteAddOn && (
                          <p className="mt-2 text-sm text-slate-600">
                            Annual termite inspection to detect any termite activity early.
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Price Display & Form */}
            <div className="lg:col-span-2 bg-slate-50 p-6 md:p-8">
              <div className="sticky top-24">
                {/* Unified Pricing Box with Logo */}
                <div className="bg-[#E74C3C] rounded-2xl overflow-hidden mb-6">
                  {/* Header with Logo and Promo */}
                  <div className="bg-[#c0392b] px-5 py-3 flex items-center justify-between">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png"
                      alt="Pest Free"
                      className="h-10"
                    />
                    <span className="text-amber-400 text-sm font-bold whitespace-nowrap">🎉 10% OFF LAUNCH SPECIAL</span>
                  </div>
                  
                  {/* Price Display */}
                  <div className="p-5 text-white">
                    <p className="text-sm text-white/80 mb-1">Your Estimated Price (inc GST)</p>
                    <div className="flex items-baseline gap-3">
                      <span className="text-xl text-white/50 line-through">${prices.pricePerVisit}</span>
                      <span className="text-4xl font-bold">${discountedPrice}</span>
                      <span className="text-sm text-white/70">per visit</span>
                    </div>
                    <p className="text-amber-400 font-semibold text-sm mt-1">You save ${launchDiscount} (10% off)</p>
                    <p className="text-xs text-red-300 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>
                    
                    {rodentTreatment && prices.rodentPrice > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/20 text-sm">
                        <div className="flex justify-between">
                          <span>+ Rodent Treatment</span>
                          <span>${prices.rodentPrice}</span>
                        </div>
                      </div>
                    )}
                    
                    {termiteAddOn && (
                      <div className="mt-3 pt-3 border-t border-white/20 text-sm">
                        <div className="flex justify-between">
                          <span>+ Termite Inspection <span className="text-[#7ABC32] text-xs">(20% off)</span></span>
                          <span>${prices.termiteInspection}</span>
                        </div>
                      </div>
                    )}
                    
                    {serviceFrequency === "sixmonthly" && (
                      <div className="mt-3 pt-3 border-t border-white/20 text-sm">
                        <div className="flex justify-between">
                          <span>Annual Total (2 visits)</span>
                          <span className="font-semibold">${Math.round(prices.annualTotal * 0.9)}</span>
                        </div>
                      </div>
                    )}
                    <div className="text-white/50 text-[10px] mt-2">Prices include GST</div>
                  </div>
                </div>
                
                {/* Contact Form */}
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Full Name *</Label>
                      <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Email *</Label>
                      <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Phone *</Label>
                      <Input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0400 000 000" className="bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Address *</Label>
                      <Textarea required value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g., 123 Example Street, Ripley QLD 4306" rows={2} className="bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Message (optional)</Label>
                      <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Any additional details..." rows={3} className="bg-white" />
                    </div>
                    
                    <p className="text-xs text-slate-500 italic">Final pricing subject to inspection.</p>
                    
                    <div className="flex gap-3">
                      <Button type="submit" disabled={submitting} className="flex-1 bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 rounded-xl font-semibold">
                        {submitting ? "Sending..." : "Get My Quote Now"} <ChevronRight className="w-5 h-5 ml-1" />
                      </Button>
                      <Button type="button" onClick={handleReset} variant="outline" className="px-4 py-6 rounded-xl">
                        <RotateCcw className="w-5 h-5" />
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Sent!</h3>
                    <p className="text-slate-600 mb-6">We'll be in touch shortly to confirm your quote.</p>
                    <Button onClick={handleReset} variant="outline" className="rounded-xl">
                      <RotateCcw className="w-4 h-4 mr-2" /> Start New Estimate
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Quick Callback Option */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 mb-3">Prefer to talk? We'll call you back within 30 minutes.</p>
          <a 
            href="tel:0737081422"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            <Phone className="w-5 h-5" />
            Call (07) 3708 1422
          </a>
        </div>
      </div>
    </section>
  );
}
